import type { ReactNode } from "react";
import { Skeleton, EmptyState, ErrorState } from "./Primitives";
import { Button } from "./Button";

export interface Column<T> {
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  keyField: (row: T) => string | number;
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  pagination?: {
    page: number;
    pageSize: number;
    hasNextPage: boolean;
    onPageChange: (page: number) => void;
  };
}

/**
 * Every list page in this app (Users, Posts, Rooms, Withdrawals, Audit Log,
 * ...) renders through this one component — new sections get the same
 * loading/error/empty/pagination behavior for free instead of
 * reimplementing it, and a future fix or polish pass here applies
 * everywhere at once.
 */
export function DataTable<T>({
  columns,
  rows,
  keyField,
  isLoading,
  error,
  onRetry,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  pagination,
}: DataTableProps<T>) {
  return (
    <div className="overflow-hidden rounded-xl border border-surface-border bg-surface-card">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-surface-border bg-surface-raised/50">
              {columns.map((col, i) => (
                <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-faint ${col.className ?? ""}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-surface-border last:border-0">
                  {columns.map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <Skeleton className="h-4 w-full max-w-[160px]" />
                    </td>
                  ))}
                </tr>
              ))}

            {!isLoading &&
              !error &&
              rows.map((row) => (
                <tr key={keyField(row)} className="border-b border-surface-border last:border-0 hover:bg-surface-raised/40">
                  {columns.map((col, i) => (
                    <td key={i} className={`px-4 py-3 align-middle text-ink ${col.className ?? ""}`}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!isLoading && error && <ErrorState message={error} onRetry={onRetry} />}

      {!isLoading && !error && rows.length === 0 && <EmptyState title={emptyTitle} description={emptyDescription} />}

      {pagination && !isLoading && !error && rows.length > 0 && (
        <div className="flex items-center justify-between border-t border-surface-border px-4 py-3">
          <p className="text-xs text-ink-faint">Page {pagination.page + 1}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={pagination.page === 0} onClick={() => pagination.onPageChange(pagination.page - 1)}>
              Previous
            </Button>
            <Button size="sm" variant="secondary" disabled={!pagination.hasNextPage} onClick={() => pagination.onPageChange(pagination.page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
